import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ShoppingCart, Gavel, Package, Clock, CheckCircle, Trophy, Tag, Check, X, MessageSquare, ArrowRight, DollarSign, HelpCircle } from "lucide-react";
import { UserAuctionCard } from "./AuctionCards";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";

// Reusable "Mis Compras" view - used both by the full /my-purchases page and the
// Header's emergent cart popup.
export const MyPurchasesContent = ({ onClose }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "purchases");
  const [purchases, setPurchases] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [myOffers, setMyOffers] = useState([]);
  const [activeProducts, setActiveProducts] = useState([]);
  const [auctionStatus, setAuctionStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  // Counter offer dialog state
  const [counterDialogOpen, setCounterDialogOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [counterAmount, setCounterAmount] = useState(0);

  // Tutorial dialog state
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [tutorialType, setTutorialType] = useState(null); // 'reservas' | 'subastas' | 'ofertas'

  useEffect(() => {
    if (authLoading) return;
    if (user && token) {
      fetchData();
      const interval = setInterval(() => {
        fetchData(true); // silent refresh
      }, 15000);
      return () => clearInterval(interval);
    } else {
      setLoading(false);
    }
  }, [user, token, authLoading]);

  const fetchData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const config = { headers, withCredentials: true };

      const [purchasesRes, bidsRes, offersRes, productsRes, auctionStatusRes] = await Promise.all([
        axios.get(`${API}/my/purchases?t=${Date.now()}`, config),
        axios.get(`${API}/my/bids?t=${Date.now()}`, config),
        axios.get(`${API}/offers/my-offers?t=${Date.now()}`, config),
        axios.get(`${API}/products?limit=6&t=${Date.now()}`),
        axios.get(`${API}/my/auction-status?t=${Date.now()}`, config)
      ]);

      setPurchases(purchasesRes.data || []);
      setMyBids(bidsRes.data || []);
      setMyOffers(offersRes.data || []);
      setActiveProducts((productsRes.data || []).filter(p => !p.is_sold));
      setAuctionStatus(auctionStatusRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (!silent) {
        setTimeout(async () => {
          try {
            const headers = { Authorization: `Bearer ${token}` };
            const config = { headers, withCredentials: true };
            const purchasesRes = await axios.get(`${API}/my/purchases?t=${Date.now()}`, config);
            setPurchases(purchasesRes.data || []);
          } catch (retryError) {
            console.error("Retry also failed:", retryError);
            toast.error("Error al cargar datos");
          }
        }, 1000);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleAcceptCounter = async (offerId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/offers/${offerId}/accept-counter`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("¡Contraoferta aceptada! El producto es tuyo.");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al aceptar contraoferta");
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectCounter = async (offerId) => {
    setProcessing(true);
    try {
      await axios.post(`${API}/offers/${offerId}/reject-counter`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Contraoferta rechazada");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al rechazar contraoferta");
    } finally {
      setProcessing(false);
    }
  };

  const openCounterDialog = (offer) => {
    setSelectedOffer(offer);
    setCounterAmount(offer.counter_amount + 10);
    setCounterDialogOpen(true);
  };

  const handleBuyerCounter = async () => {
    if (!selectedOffer) return;
    setProcessing(true);
    try {
      await axios.post(`${API}/offers/${selectedOffer.offer_id}/buyer-counter`, { amount: counterAmount }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("¡Contraoferta enviada al vendedor!");
      setCounterDialogOpen(false);
      setSelectedOffer(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar contraoferta");
    } finally {
      setProcessing(false);
    }
  };

  const getBidStatus = (bid) => {
    if (bid.is_winner) {
      return (
        <span className="flex items-center gap-1 px-2 py-1 bg-[#10B981]/10 text-[#10B981] rounded-full text-xs font-medium">
          <Trophy size={12} />
          ¡Ganaste!
        </span>
      );
    }
    if (bid.is_highest) {
      return (
        <span className="flex items-center gap-1 px-2 py-1 bg-[#3B82F6]/10 text-[#3B82F6] rounded-full text-xs font-medium">
          <Gavel size={12} />
          Oferta más alta
        </span>
      );
    }
    if (bid.auction_ended) {
      return (
        <span className="flex items-center gap-1 px-2 py-1 bg-[#64748B]/10 text-[#64748B] rounded-full text-xs font-medium">
          Superado
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 px-2 py-1 bg-[#F59E0B]/10 text-[#F59E0B] rounded-full text-xs font-medium">
        <Clock size={12} />
        En curso
      </span>
    );
  };

  // Group user's bids by product for collapsible display
  const groupedUserBids = myBids.reduce((acc, bid) => {
    const pid = bid.product_id;
    if (!acc[pid]) {
      acc[pid] = {
        productId: pid,
        productTitle: bid.product_title,
        productImage: bid.product_image,
        currentHighestBid: bid.current_bid,
        endTime: bid.end_time,
        isWinning: bid.is_highest || bid.is_winner,
        auctionEnded: bid.auction_ended,
        bids: []
      };
    }
    if (bid.is_highest || bid.is_winner) {
      acc[pid].isWinning = true;
    }
    acc[pid].currentHighestBid = Math.max(acc[pid].currentHighestBid || 0, bid.current_bid || 0);
    acc[pid].bids.push(bid);
    return acc;
  }, {});

  const groupedUserBidsArray = Object.values(groupedUserBids);

  const getOfferStatusBadge = (offer) => {
    switch (offer.status) {
      case "pending":
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-[#F59E0B]/10 text-[#F59E0B] rounded-full text-xs font-medium">
            <Clock size={12} />
            Pendiente
          </span>
        );
      case "accepted":
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-[#10B981]/10 text-[#10B981] rounded-full text-xs font-medium">
            <CheckCircle size={12} />
            Aceptada
          </span>
        );
      case "rejected":
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-[#EF4444]/10 text-[#EF4444] rounded-full text-xs font-medium">
            <X size={12} />
            Rechazada
          </span>
        );
      case "counter":
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-[#8B5CF6]/10 text-[#8B5CF6] rounded-full text-xs font-medium">
            <MessageSquare size={12} />
            Contraoferta
          </span>
        );
      case "expired":
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-[#94A3B8]/10 text-[#94A3B8] rounded-full text-xs font-medium">
            <Clock size={12} />
            Expirada
          </span>
        );
      default:
        return null;
    }
  };

  const getPurchaseStatusBadge = (product) => {
    const status = product.purchase_intent_status;

    if (product.paid) {
      return (
        <span className="flex items-center gap-1 px-2 py-1 bg-[#3B82F6] text-white rounded-full text-xs font-medium" data-testid="badge-paid">
          <CheckCircle size={12} />
          Pagado
        </span>
      );
    }

    if (product.status === "reserved" || status === "accepted") {
      return (
        <span className="flex items-center gap-1 px-2 py-1 bg-[#10B981] text-white rounded-full text-xs font-medium" data-testid="badge-reserved">
          <CheckCircle size={12} />
          Reservado
        </span>
      );
    }

    switch (status) {
      case "pending":
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-[#F59E0B] text-white rounded-full text-xs font-medium" data-testid="badge-pending">
            <Clock size={12} />
            Solicitado
          </span>
        );
      case "paid":
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-[#3B82F6] text-white rounded-full text-xs font-medium" data-testid="badge-paid">
            <CheckCircle size={12} />
            Pagado
          </span>
        );
      case "rejected":
      case "cancelled":
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-[#EF4444] text-white rounded-full text-xs font-medium" data-testid="badge-rejected">
            <X size={12} />
            Rechazado
          </span>
        );
      case "revoked":
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-[#EF4444] text-white rounded-full text-xs font-medium" data-testid="badge-revoked">
            <X size={12} />
            Revocado
          </span>
        );
      default:
        if (product.winner_id === user?.user_id) {
          if (product.is_sold && product.paid) {
            return (
              <span className="flex items-center gap-1 px-2 py-1 bg-[#3B82F6] text-white rounded-full text-xs font-medium" data-testid="badge-paid">
                <CheckCircle size={12} />
                Pagado
              </span>
            );
          }
          return (
            <span className="flex items-center gap-1 px-2 py-1 bg-[#10B981]/10 text-[#10B981] rounded-full text-xs font-medium" data-testid="badge-winner">
              <Trophy size={12} />
              ¡Ganaste!
            </span>
          );
        }
        return null;
    }
  };

  const counterOffers = myOffers.filter(o => o.status === "counter");

  const tutorialContent = {
    reservas: {
      title: "¿Cómo funcionan las Reservas?",
      steps: [
        "Cuando solicitas un producto de Venta Directa, aparece aquí con estado 'Solicitado'.",
        "El vendedor revisa tu solicitud y puede aceptarla (pasa a 'Reservado').",
        "Una vez reservado, coordinas el pago y entrega con el vendedor.",
        "Después del pago, el estado cambia a 'Pagado' y ¡el producto es tuyo!"
      ]
    },
    subastas: {
      title: "¿Cómo funcionan las Subastas?",
      steps: [
        "Cada puja que haces en una subasta aparece aquí agrupada por producto.",
        "Si ves '✓ GANANDO' significa que tu puja es la más alta actualmente.",
        "Si ves '✗ SUPERADO', alguien pujó más - ¡puedes aumentar tu oferta!",
        "Cuando la subasta termina, si ganaste, el producto pasa a tus Reservas."
      ]
    },
    ofertas: {
      title: "¿Cómo funcionan las Ofertas?",
      steps: [
        "Cuando envías una oferta por un producto, aparece aquí con estado 'Pendiente'.",
        "El vendedor puede Aceptar, Rechazar o enviarte una Contraoferta.",
        "Si recibes contraoferta, puedes aceptarla, rechazarla o negociar (máx. 3 intentos).",
        "Una oferta aceptada reserva el producto para ti automáticamente."
      ]
    }
  };

  const openTutorial = (type) => {
    setTutorialType(type);
    setTutorialOpen(true);
  };

  const goBack = () => {
    if (onClose) onClose();
    else navigate(-1);
  };

  const closeThenNavigate = () => onClose?.();

  return (
    <div data-testid="my-purchases-content">
      <button
        onClick={goBack}
        className="flex items-center gap-2 text-[#64748B] hover:text-[#0F172A] mb-4"
        data-testid="cart-close-button"
      >
        <ArrowLeft size={18} />
        Volver
      </button>

      <h1 className="text-xl font-bold text-[#0F172A] mb-4 flex items-center gap-2">
        <ShoppingCart size={22} />
        Mis Compras
      </h1>

      {/* Counter Offers Alert */}
      {counterOffers.length > 0 && (
        <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-xl">
          <h3 className="font-semibold text-purple-800 flex items-center gap-2 mb-3">
            <MessageSquare size={18} />
            Tienes {counterOffers.length} contraoferta{counterOffers.length > 1 ? 's' : ''} pendiente{counterOffers.length > 1 ? 's' : ''}
          </h3>
          <div className="space-y-3">
            {counterOffers.map((offer) => {
              const totalAttempts = (offer.offer_count || 1);
              const canCounterOffer = totalAttempts < 3;

              return (
                <div key={offer.offer_id} className="bg-white rounded-lg p-3 border border-purple-100">
                  <div className="flex items-center gap-3">
                    <img src={offer.product_image} alt={offer.product_title} className="w-12 h-12 object-cover rounded" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#0F172A] truncate">{offer.product_title}</p>
                      <p className="text-sm text-[#64748B]">
                        Tu oferta: <span className="line-through">${offer.amount}</span> →
                        Contraoferta: <span className="font-bold text-purple-600">${offer.counter_amount}</span>
                      </p>
                      <p className="text-xs text-[#94A3B8]">Intentos usados: {totalAttempts}/3</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button
                      size="sm"
                      onClick={() => handleAcceptCounter(offer.offer_id)}
                      disabled={processing}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                      data-testid={`accept-counter-${offer.offer_id}`}
                    >
                      <Check size={16} className="mr-1" />
                      Aceptar ${offer.counter_amount}
                    </Button>
                    {canCounterOffer ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openCounterDialog(offer)}
                        disabled={processing}
                        className="flex-1 border-purple-500 text-purple-500 hover:bg-purple-50"
                        data-testid={`counter-offer-${offer.offer_id}`}
                      >
                        <ArrowRight size={16} className="mr-1" />
                        Contraofertar
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRejectCounter(offer.offer_id)}
                        disabled={processing}
                        className="flex-1 border-red-500 text-red-500 hover:bg-red-50"
                        data-testid={`reject-counter-${offer.offer_id}`}
                      >
                        <X size={16} className="mr-1" />
                        Rechazar
                      </Button>
                    )}
                  </div>
                  {!canCounterOffer && (
                    <p className="text-xs text-orange-600 mt-2 text-center">
                      Has usado tus 3 intentos. Solo puedes aceptar o rechazar.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full bg-[#F1F5F9] rounded-full p-1 mb-6">
          <TabsTrigger value="purchases" className="flex-1 rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm relative" data-testid="tab-purchases">
            <Package size={16} className="mr-1.5" />
            <span className="text-xs sm:text-sm">Reservas</span>
            {purchases.length > 0 && <span className="ml-1 bg-[#10B981] text-white text-[10px] px-1.5 rounded-full">{purchases.length}</span>}
            <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); openTutorial('reservas'); }} className="ml-1 text-[#94A3B8] hover:text-[#475569]" data-testid="help-reservas">
              <HelpCircle size={14} />
            </span>
          </TabsTrigger>
          <TabsTrigger value="bids" className="flex-1 rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm relative" data-testid="tab-bids">
            <Gavel size={16} className="mr-1.5" />
            <span className="text-xs sm:text-sm">Subastas</span>
            {myBids.length > 0 && <span className="ml-1 bg-[#3B82F6] text-white text-[10px] px-1.5 rounded-full">{myBids.length}</span>}
            <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); openTutorial('subastas'); }} className="ml-1 text-[#94A3B8] hover:text-[#475569]" data-testid="help-subastas">
              <HelpCircle size={14} />
            </span>
          </TabsTrigger>
          <TabsTrigger value="offers" className="flex-1 rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm relative" data-testid="tab-offers">
            <Tag size={16} className="mr-1.5" />
            <span className="text-xs sm:text-sm">Ofertas</span>
            {myOffers.length > 0 && <span className="ml-1 bg-[#F59E0B] text-white text-[10px] px-1.5 rounded-full">{myOffers.length}</span>}
            <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); openTutorial('ofertas'); }} className="ml-1 text-[#94A3B8] hover:text-[#475569]" data-testid="help-ofertas">
              <HelpCircle size={14} />
            </span>
          </TabsTrigger>
        </TabsList>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            {/* Purchases Tab */}
            <TabsContent value="purchases" className="mt-0">
              {purchases.length === 0 ? (
                <div className="text-center py-8">
                  <ShoppingCart size={48} className="mx-auto text-[#CBD5E1] mb-4" />
                  <h3 className="text-lg font-semibold text-[#0F172A] mb-2">No tienes compras aún</h3>
                  <p className="text-[#64748B] mb-6">¡Explora nuestros productos y participa!</p>

                  {activeProducts.length > 0 && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-3">
                        {activeProducts.slice(0, 6).map((product) => (
                          <Link
                            key={product.product_id}
                            to={`/product/${product.product_id}`}
                            onClick={closeThenNavigate}
                            className="aspect-square rounded-xl overflow-hidden bg-[#F1F5F9] hover:opacity-80 transition-opacity"
                          >
                            <img src={product.image_url} alt={product.title} className="w-full h-full object-cover" />
                          </Link>
                        ))}
                      </div>
                      <Link to="/" onClick={closeThenNavigate}>
                        <Button className="rounded-full bg-[#475569] hover:bg-[#334155]">
                          Ir a Productos
                          <ArrowRight size={18} className="ml-2" />
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4" data-testid="purchases-list">
                  {purchases.map((product) => (
                    <Link
                      key={product.product_id}
                      to={`/product/${product.product_id}`}
                      onClick={closeThenNavigate}
                      className="bg-white rounded-xl border border-[#E2E8F0] p-4 flex gap-4 hover:border-[#CBD5E1] transition-colors block"
                      data-testid={`purchase-${product.product_id}`}
                    >
                      <div className="w-20 h-20 rounded-lg overflow-hidden bg-[#F1F5F9] flex-shrink-0">
                        <img src={product.image_url} alt={product.title} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-semibold text-[#0F172A] line-clamp-1">{product.title}</h3>
                          {getPurchaseStatusBadge(product)}
                        </div>
                        <p className="text-sm text-[#64748B] line-clamp-1 mt-1">{product.user_name}</p>
                        <div className="flex items-center justify-between mt-2">
                          <p className="text-lg font-bold text-[#10B981]">
                            ${(product.final_price || product.buy_now_price || product.price)?.toLocaleString()}
                          </p>
                          <span className="text-xs text-[#64748B]">
                            {product.sale_type === "auction"
                              ? (product.winner_id === user?.user_id ? "Subasta ganada" : "Comprar Ahora")
                              : "Compra directa"}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* My Bids Tab */}
            <TabsContent value="bids" className="mt-0">
              {groupedUserBidsArray.length === 0 ? (
                <div className="text-center py-12">
                  <Gavel size={64} className="mx-auto text-[#CBD5E1] mb-4" />
                  <h3 className="text-lg font-semibold text-[#0F172A]">No tienes pujas</h3>
                  <p className="text-[#64748B]">Tus participaciones en subastas aparecerán aquí</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="bids-list">
                  {groupedUserBidsArray.map((group) => (
                    <UserAuctionCard
                      key={group.productId}
                      productId={group.productId}
                      productTitle={group.productTitle}
                      productImage={group.productImage}
                      userBids={group.bids}
                      currentHighestBid={group.currentHighestBid}
                      isWinning={group.isWinning}
                      endTime={group.endTime}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            {/* My Offers Tab */}
            <TabsContent value="offers" className="mt-0">
              {myOffers.length === 0 ? (
                <div className="text-center py-12">
                  <Tag size={64} className="mx-auto text-[#CBD5E1] mb-4" />
                  <h3 className="text-lg font-semibold text-[#0F172A]">No tienes ofertas</h3>
                  <p className="text-[#64748B]">Tus ofertas enviadas aparecerán aquí</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="offers-list">
                  {myOffers.map((offer) => (
                    <div key={offer.offer_id} className="bg-white rounded-xl border border-[#E2E8F0] p-4" data-testid={`offer-${offer.offer_id}`}>
                      <Link to={`/product/${offer.product_id}`} onClick={closeThenNavigate} className="flex gap-4 hover:opacity-80 transition-opacity">
                        <div className="w-20 h-20 rounded-lg overflow-hidden bg-[#F1F5F9] flex-shrink-0">
                          <img src={offer.product_image} alt={offer.product_title} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-semibold text-[#0F172A] line-clamp-1">{offer.product_title}</h3>
                            {getOfferStatusBadge(offer)}
                          </div>
                          <div className="flex items-center gap-4 mt-2">
                            <div>
                              <span className="text-xs text-[#64748B]">Precio</span>
                              <p className="font-bold text-[#64748B]">${offer.product_price?.toLocaleString()}</p>
                            </div>
                            <div>
                              <span className="text-xs text-[#64748B]">Tu oferta</span>
                              <p className="font-bold text-[#0F172A]">${offer.amount?.toLocaleString()}</p>
                            </div>
                            {offer.counter_amount && (
                              <div>
                                <span className="text-xs text-[#64748B]">Contraoferta</span>
                                <p className="font-bold text-purple-600">${offer.counter_amount?.toLocaleString()}</p>
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-[#64748B] mt-2">
                            Enviada {formatDistanceToNow(new Date(offer.created_at), { addSuffix: true, locale: es })}
                          </p>
                        </div>
                      </Link>

                      {offer.status === "counter" && (
                        <div className="flex gap-2 mt-3 pt-3 border-t border-[#E2E8F0]">
                          <Button size="sm" onClick={() => handleAcceptCounter(offer.offer_id)} disabled={processing} className="flex-1 bg-green-500 hover:bg-green-600 text-white">
                            <Check size={16} className="mr-1" />
                            Aceptar ${offer.counter_amount}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleRejectCounter(offer.offer_id)} disabled={processing} className="flex-1 border-red-500 text-red-500 hover:bg-red-50">
                            <X size={16} className="mr-1" />
                            Rechazar
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </>
        )}
      </Tabs>

      {/* Buyer Counter Offer Dialog */}
      <Dialog open={counterDialogOpen} onOpenChange={setCounterDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="text-purple-600" size={20} />
              Enviar Contraoferta
            </DialogTitle>
          </DialogHeader>
          {selectedOffer && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-3">
                <img src={selectedOffer.product_image} alt={selectedOffer.product_title} className="w-16 h-16 object-cover rounded-lg" />
                <div>
                  <p className="font-semibold text-[#0F172A]">{selectedOffer.product_title}</p>
                  <p className="text-sm text-[#64748B]">Contraoferta del vendedor</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="p-3 bg-[#F1F5F9] rounded-xl">
                  <p className="text-xs text-[#64748B]">Tu oferta original</p>
                  <p className="text-xl font-bold text-[#64748B] line-through">${selectedOffer.amount}</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-xl">
                  <p className="text-xs text-purple-600">Contraoferta del vendedor</p>
                  <p className="text-xl font-bold text-purple-600">${selectedOffer.counter_amount}</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-[#0F172A]">Tu nueva contraoferta</label>
                <p className="text-xs text-[#64748B] mb-2">Debe ser mayor a ${selectedOffer.counter_amount}</p>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-[#10B981]">$</span>
                  <Input
                    type="number"
                    value={counterAmount}
                    onChange={(e) => setCounterAmount(Number(e.target.value))}
                    min={selectedOffer.counter_amount + 1}
                    className="text-2xl font-bold h-14"
                    data-testid="buyer-counter-amount-input"
                  />
                </div>
                {counterAmount <= selectedOffer.counter_amount && (
                  <p className="text-xs text-red-500 mt-1">El monto debe ser mayor a ${selectedOffer.counter_amount}</p>
                )}
              </div>

              <p className="text-xs text-center text-[#94A3B8]">Intentos usados: {(selectedOffer.offer_count || 1)}/3</p>
            </div>
          )}
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setCounterDialogOpen(false)} className="flex-1 rounded-full">
              Cancelar
            </Button>
            <Button
              onClick={handleBuyerCounter}
              disabled={processing || !selectedOffer || counterAmount <= selectedOffer?.counter_amount}
              className="flex-1 rounded-full h-12 bg-purple-500 hover:bg-purple-600"
              data-testid="send-buyer-counter-button"
            >
              {processing ? <div className="spinner" style={{ width: 20, height: 20 }} /> : (
                <>
                  <ArrowRight size={18} className="mr-2" />
                  Enviar ${counterAmount}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tutorial Dialog */}
      <Dialog open={tutorialOpen} onOpenChange={setTutorialOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="text-[#475569]" size={20} />
              {tutorialType && tutorialContent[tutorialType]?.title}
            </DialogTitle>
          </DialogHeader>
          {tutorialType && (
            <div className="space-y-4 py-4">
              <ol className="space-y-3">
                {tutorialContent[tutorialType]?.steps.map((step, idx) => (
                  <li key={idx} className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#475569] text-white text-sm flex items-center justify-center font-semibold">
                      {idx + 1}
                    </span>
                    <p className="text-sm text-[#374151] pt-0.5">{step}</p>
                  </li>
                ))}
              </ol>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setTutorialOpen(false)} className="w-full rounded-full bg-[#475569] hover:bg-[#334155]">
              ¡Entendido!
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyPurchasesContent;
